/**
 * Alert Banner Component (Phase 3 — Step 3.7).
 *
 * Displays active alerts as dismissible banners.
 */

import React from 'react';
import { AlertOctagon, AlertTriangle, Info } from 'lucide-react';
import { useAppStore } from '@/store';
import type { Alert, AlertSeverity } from '@/notifications/alertEngine';

function severityClass(severity: AlertSeverity): string {
  switch (severity) {
    case 'danger': return 'alert-banner--danger';
    case 'warning': return 'alert-banner--warning';
    case 'info': return 'alert-banner--info';
  }
}

function severityIcon(severity: AlertSeverity): React.ReactElement {
  switch (severity) {
    case 'danger': return <AlertOctagon aria-hidden="true" />;
    case 'warning': return <AlertTriangle aria-hidden="true" />;
    case 'info': return <Info aria-hidden="true" />;
  }
}

export function AlertBanner(): React.ReactElement | null {
  const alerts = useAppStore((s) => s.alerts);
  const dismissAlert = useAppStore((s) => s.dismissAlert);

  const active = alerts.filter((a: Alert) => !a.dismissed);

  if (active.length === 0) return null;

  return (
    <div className="alert-banner-container" role="region" aria-label="Active Alerts" aria-live="polite">
      {active.slice(0, 5).map((alert: Alert) => (
        <div
          key={alert.id}
          className={`alert-banner ${severityClass(alert.severity)}`}
          role="alert"
        >
          <span className="alert-banner__icon">{severityIcon(alert.severity)}</span>
          <span className="alert-banner__message">{alert.message}</span>
          <button
            className="alert-banner__dismiss"
            onClick={() => dismissAlert(alert.id)}
            aria-label="Dismiss alert"
            type="button"
          >
            &times;
          </button>
        </div>
      ))}
    </div>
  );
}
